import {
  decodePairingCode,
  type FailReason,
  type Hello,
  PairingClient,
  type PeerEvent,
  PING_EVERY_MS,
  type Role,
  type Session,
  SILENCE_LIMIT_MS,
} from '@iws/pos-companion'

export const failText = (r: FailReason) =>
  ({
    busy: 'That device already has a companion. Disconnect it first.',
    protocol: 'The connection failed.',
    bad_pin: 'Wrong code.',
    locked: 'Wrong code three times. Ask for a new code.',
    version: 'Update the app on one of the devices.',
    unknown_session: 'Pair again.',
    not_pairing: 'That device is not pairing. Start pairing there first.',
  })[r]

export interface LinkHandlers {
  onEvent: (e: PeerEvent) => void
  onReady: () => void
  onOffline: () => void
  onEnded: (message: string) => void
}

/**
 * One pairing from a browser to a pos-app host over `ws://` on the LAN (Chrome asks for local-network access once).
 * Reconnects with the session for about two minutes; a reloaded page needs the code again (POS_COMPANION.md §7).
 */
export const openLink = (code: string, role: Role, h: LinkHandlers, hello?: Omit<Hello, 'type' | 'v'>) => {
  const { host, port, pin } = decodePairingCode(code)
  let session: Session | undefined
  let socket: WebSocket | undefined
  let client: PairingClient<Role> | undefined
  let stopped = false
  let tries = 0
  let lastSeen = Date.now()

  const finish = (message: string) => {
    if (stopped) return
    stopped = true
    clearInterval(timer)
    socket?.close()
    h.onEnded(message)
  }

  const connect = () => {
    let failed: FailReason | undefined
    const ws = new WebSocket(`ws://${host}:${port}/ws`)
    socket = ws
    const c = new PairingClient({
      role,
      hello,
      link: { send: (f) => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify(f)), close: () => ws.close() },
      onEvent: (e) => {
        lastSeen = Date.now()
        h.onEvent(e)
      },
      onReady: (s) => {
        session = s
        tries = 0
        lastSeen = Date.now()
        h.onReady()
      },
      onFailed: (r) => (failed = r),
    })
    client = c
    ws.onopen = () => (session ? c.resume(session) : c.pair(pin))
    ws.onmessage = (m) => {
      if (typeof m.data !== 'string') return ws.close()
      try {
        c.receive(JSON.parse(m.data))
      } catch {
        ws.close()
      }
    }
    ws.onclose = () => {
      c.closed()
      client = undefined
      if (stopped) return
      if (failed && (!session || failed !== 'protocol')) return finish(failText(failed))
      if (!session && tries >= 2) return finish(`Could not reach ${host}. Check both devices are on the same network.`)
      if (tries++ > 40) return finish('Disconnected')
      h.onOffline()
      setTimeout(() => !stopped && connect(), 3000)
    }
  }

  const timer = setInterval(() => {
    if (!client || role !== 'companion') return
    if (Date.now() - lastSeen > SILENCE_LIMIT_MS && session) socket?.close()
    else client.send({ type: 'ping' } as never)
  }, PING_EVERY_MS)

  connect()
  return {
    send: (m: Parameters<PairingClient<Role>['send']>[0]) => client?.send(m as never),
    stop: (message = 'Unpaired') => finish(message),
  }
}
